import { useMemo, type ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ShowDetailsForm } from '@/components/ShowDetailsForm';
import { type Happening } from '@littlebooker/dal';
import { apiGetPublic } from '@/lib/api';

type BannerVariant = 'info' | 'success' | 'warning' | 'error';

type StatusBannerProps = {
    icon?: ReactNode;
    title: string;
    body: string;
    variant: BannerVariant;
};

const bannerStyles: Record<BannerVariant, string> = {
    info: 'bg-blue-50 text-blue-900 border-blue-100',
    success: 'bg-emerald-50 text-emerald-900 border-emerald-100',
    warning: 'bg-amber-50 text-amber-900 border-amber-100',
    error: 'bg-red-50 text-red-900 border-red-100',
};

function StatusBanner({ icon, title, body, variant }: StatusBannerProps) {
    return (
        <div className={`flex items-start gap-3 rounded-lg border px-4 py-3 text-sm ${bannerStyles[variant]}`}>
            <div className="mt-0.5 text-lg">{icon}</div>
            <div>
                <p className="font-semibold">{title}</p>
                <p className="text-sm/relaxed opacity-90">{body}</p>
            </div>
        </div>
    );
}

export default function ShowInvitePage() {
    const { slug } = useParams<{ slug: string }>();
    const [searchParams] = useSearchParams();
    const stubToken = searchParams.get('stub') ?? '';

    const {
        data: happening,
        isLoading,
        isError,
        error,
    } = useQuery({
        // Fetch by stub_token via the public API (server validates + bypasses
        // RLS); slug is only for display/routing now. Closes §5.3 hole (a).
        queryKey: ['public-show', stubToken],
        queryFn: () => apiGetPublic<Happening>('/show', { token: stubToken }),
        enabled: Boolean(stubToken),
    });

    const stubIsValid = useMemo(() => {
        if (!happening || !stubToken) return false;
        return Boolean(happening.metadata?.stub_token && happening.metadata.stub_token === stubToken);
    }, [stubToken, happening]);

    const renderState = () => {
        if (!slug) {
            return (
                <StatusBanner
                    variant="error"
                    title="Missing show identifier"
                    body="We couldn't figure out which show this link belongs to. Double-check the link you were sent."
                    icon={<AlertTriangle className="h-5 w-5" />}
                />
            );
        }

        if (!stubToken) {
            return (
                <StatusBanner
                    variant="warning"
                    title="Stub token missing"
                    body="This link is missing the stub token. Ask your LittleBooker admin to resend it or copy the full link again."
                    icon={<AlertTriangle className="h-5 w-5" />}
                />
            );
        }

        if (isLoading) {
            return (
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Checking your link…
                </div>
            );
        }

        if (isError) {
            return (
                <StatusBanner
                    variant="error"
                    title="Something went wrong"
                    body={
                        error instanceof Error
                            ? error.message
                            : 'We could not load your show yet. Refresh the page or try again later.'
                    }
                    icon={<AlertTriangle className="h-5 w-5" />}
                />
            );
        }

        if (!happening) {
            return (
                <StatusBanner
                    variant="error"
                    title="We can't find that show"
                    body="The link points to a show that doesn't exist. Ask your admin to double-check the slug or send a new link."
                    icon={<AlertTriangle className="h-5 w-5" />}
                />
            );
        }

        if (!happening.metadata?.stub_token) {
            return (
                <StatusBanner
                    variant="warning"
                    title="Link not enabled yet"
                    body="This show doesn't have a stub token on file. Let your LittleBooker admin know so they can generate a new one."
                    icon={<AlertTriangle className="h-5 w-5" />}
                />
            );
        }

        if (!stubIsValid) {
            return (
                <StatusBanner
                    variant="error"
                    title="Link expired or invalid"
                    body="The stub token in this link doesn't match what we have on file. Request a fresh link to continue."
                    icon={<AlertTriangle className="h-5 w-5" />}
                />
            );
        }

        return (
            <div className="space-y-6">
                <StatusBanner
                    variant="success"
                    title="Link confirmed"
                    body="You're in! Fill out the details below for your upcoming show."
                    icon={<CheckCircle2 className="h-5 w-5" />}
                />

                <ShowDetailsForm happening={happening} stubToken={stubToken} />
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-background to-muted/50">
            <div className="mx-auto flex min-h-screen max-w-3xl flex-col justify-center gap-6 p-6">
                <div className="text-center">
                    <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
                        LittleBooker · Show Portal
                    </p>
                    <h1 className="mt-2 text-3xl font-bold tracking-tight">Complete your show details</h1>
                    <p className="mt-2 text-sm text-muted-foreground">
                        Use the link your host sent you to fill in your show information.
                    </p>
                </div>
                <Card className="border-primary/10 shadow-xl">
                    <CardHeader>
                        <CardTitle className="text-2xl">Show details</CardTitle>
                        <CardDescription>
                            Verify your link, then fill in the details for your upcoming show.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">{renderState()}</CardContent>
                </Card>
            </div>
        </div>
    );
}
